"use client";

import type { QueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  createTRPCClient,
  httpBatchLink,
  loggerLink,
  unstable_httpBatchStreamLink,
} from "@trpc/client";
import { createTRPCReact } from "@trpc/react-query";
import SuperJSON from "superjson";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@constructr-sdk/ui/components/alert-dialog";

import type { AppRouter } from "@constructr-sdk/api";

import { env } from "../env";
import { createQueryClient } from "./query-client";

let clientQueryClientSingleton: QueryClient | undefined = undefined;
const getQueryClient = () => {
  if (typeof window === "undefined") {
    // Server: always make a new query client
    return createQueryClient();
  } else {
    // Browser: use singleton pattern to keep the same query client
    return (clientQueryClientSingleton ??= createQueryClient());
  }
};

const getBaseUrl = () => {
  if (typeof window !== "undefined") return window.location.origin;
  // eslint-disable-next-line no-restricted-properties
  return `http://localhost:${process.env.PORT ?? 3000}`;
};

export const trpcVanillaClient = createTRPCClient<AppRouter>({
  links: [
    httpBatchLink({
      transformer: SuperJSON,
      url: getBaseUrl() + "/api/trpc",
      headers() {
        const headers = new Headers();
        headers.set("x-trpc-source", "nextjs-react");
        return headers;
      },
    }),
  ],
});

export const trpcReactQueryApi = createTRPCReact<AppRouter>();

// Track if error dialog is currently shown to prevent multiple instances
let isErrorDialogShown = false;

export function TRPCReactProvider(props: { children: React.ReactNode }) {
  const queryClient = getQueryClient();
  const [errorDetails, setErrorDetails] = useState<string | null>(null);
  const [errorPath, setErrorPath] = useState<string | null>(null);
  const [isAlertOpen, setIsAlertOpen] = useState(false);

  // Function to copy error details to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        toast.success('Error details copied to clipboard');
      })
      .catch((err) => {
        console.error('Failed to copy text: ', err);
        toast.error('Failed to copy error details');
      });
  };

  const [trpcClient] = useState(() =>
    trpcReactQueryApi.createClient({

      links: [
        loggerLink({
          enabled: (op) =>
            process.env.NODE_ENV === "development" ||
            (op.direction === "down" && op.result instanceof Error),
          console: {
            log: (...args) => console.log(...args),
            error: (...args) => {
              if (process.env.NEXT_PUBLIC_PREVIEW_MODE === "true") {
                const errorObj = args.find(arg => typeof arg === 'object' && arg !== null);
                // Only show the error dialog if it's not already shown
                if (errorObj?.result?.data && !isErrorDialogShown) {
                  isErrorDialogShown = true;
                  const errorData = JSON.stringify(errorObj.result.data, null, 2);
                  setErrorDetails(errorData);
                  setErrorPath(errorObj.result?.data?.path);
                  setIsAlertOpen(true);
                }
              }
            },
          },
        }),
        unstable_httpBatchStreamLink({
          transformer: SuperJSON,
          url: getBaseUrl() + "/api/trpc",
          headers() {
            const headers = new Headers();
            headers.set("x-trpc-source", "nextjs-react");
            return headers;
          },
        }),
      ],
    }),
  );

  return (
    <QueryClientProvider client={queryClient}>
      <trpcReactQueryApi.Provider client={trpcClient} queryClient={queryClient}>
        {props.children}
      </trpcReactQueryApi.Provider>

      {/* Error Alert Dialog */}
      <AlertDialog open={isAlertOpen} onOpenChange={(open) => {
        setIsAlertOpen(open);
        if (!open) {
          isErrorDialogShown = false;
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Runtime Error</AlertDialogTitle>
            <AlertDialogDescription>
              An error occurred while processing your request {errorPath}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {errorDetails && (
            <div className="mt-2 max-h-[300px] overflow-auto rounded bg-muted p-4">
              <pre className="text-xs">{errorDetails}</pre>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Close</AlertDialogCancel>
            <AlertDialogAction onClick={() => errorDetails && copyToClipboard(errorDetails)}>
              Copy Details
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </QueryClientProvider>
  );
}
